import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { AuditEntry, ClientRecord } from '../domain/types';

interface SentinelDB extends DBSchema {
  clients: {
    key: string;
    value: ClientRecord;
    indexes: { 'by-branch': string; 'by-date': string };
  };
  audit: {
    key: string;
    value: AuditEntry;
    indexes: { 'by-client': string; 'by-timestamp': string };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown };
  };
}

const DB_NAME = 'sentinel';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<SentinelDB>> | null = null;

function db(): Promise<IDBPDatabase<SentinelDB>> {
  if (!dbPromise) {
    dbPromise = openDB<SentinelDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const clients = database.createObjectStore('clients', { keyPath: 'client_id' });
        clients.createIndex('by-branch', 'branch');
        clients.createIndex('by-date', 'onboarding_date');

        const audit = database.createObjectStore('audit', { keyPath: 'id' });
        audit.createIndex('by-client', 'clientId');
        audit.createIndex('by-timestamp', 'timestamp');

        database.createObjectStore('meta', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

export async function putClient(record: ClientRecord): Promise<void> {
  const handle = await db();
  await handle.put('clients', record);
}

export async function putClients(records: ClientRecord[]): Promise<void> {
  const handle = await db();
  const tx = handle.transaction('clients', 'readwrite');
  await Promise.all(records.map((r) => tx.store.put(r)));
  await tx.done;
}

export async function getAllClients(): Promise<ClientRecord[]> {
  const handle = await db();
  return handle.getAll('clients');
}

export async function getClient(clientId: string): Promise<ClientRecord | undefined> {
  const handle = await db();
  return handle.get('clients', clientId);
}

/**
 * Append-only audit log. Records cannot be modified or deleted through the
 * application surface — modification would require a dedicated, gated admin
 * tool (out of scope for the prototype; see ARCHITECTURE.md).
 */
export async function appendAudit(entry: AuditEntry): Promise<void> {
  const handle = await db();
  await handle.add('audit', entry);
}

export async function getAuditForClient(clientId: string): Promise<AuditEntry[]> {
  const handle = await db();
  const entries = await handle.getAllFromIndex('audit', 'by-client', clientId);
  return entries.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const handle = await db();
  const row = await handle.get('meta', key);
  return row?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const handle = await db();
  await handle.put('meta', { key, value });
}

/** Test-only: tear down the IndexedDB connection so a fresh one can be opened. */
export function _resetDbHandleForTests(): void {
  dbPromise = null;
}
